import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
  BorrowingDueItem, Covenant, CovenantReport, CovenantRequest, Drawdown, DrawdownRequest, Facility, FacilityRequest,
  InterestLine, InterestRun, MaturityLadderReport, OdPositionReport, RateChange, Repayment, RepaymentPreview,
  RepaymentRequest, TieOutReport, UtilisationReport,
} from '../interfaces/borrowing.interface';

const E = ENDPOINTS.BORROWING;
const asOfParams = (asOf?: string) => (asOf ? { params: { asOf } } : undefined);

export const BorrowingService = {
  listFacilities: async (institutionId: string): Promise<Facility[]> => (await axios.get(E.FACILITIES(institutionId))).data,
  getFacility: async (id: string): Promise<Facility> => (await axios.get(E.FACILITY(id))).data,
  createFacility: async (body: FacilityRequest): Promise<Facility> => (await axios.post(E.FACILITY_CREATE, body)).data,
  updateFacility: async (id: string, body: FacilityRequest): Promise<Facility> => (await axios.put(E.FACILITY(id), body)).data,
  closeFacility: async (id: string): Promise<Facility> => (await axios.post(E.FACILITY_CLOSE(id))).data,
  reopenFacility: async (id: string): Promise<Facility> => (await axios.post(E.FACILITY_REOPEN(id))).data,
  addRate: async (id: string, body: { effectiveDate: string; ratePct: number; note?: string }): Promise<RateChange> =>
    (await axios.post(E.RATE_ADD(id), body)).data,
  deleteRate: async (id: string, rateId: string): Promise<void> => { await axios.delete(E.RATE_DELETE(id, rateId)); },

  listDrawdowns: async (facilityId: string): Promise<Drawdown[]> => (await axios.get(E.DRAWDOWNS(facilityId))).data,
  getDrawdown: async (id: string): Promise<Drawdown> => (await axios.get(E.DRAWDOWN(id))).data,
  createDrawdown: async (body: DrawdownRequest): Promise<Drawdown> => (await axios.post(E.DRAWDOWN_CREATE, body)).data,
  voidDrawdown: async (id: string): Promise<Drawdown> => (await axios.post(E.DRAWDOWN_VOID(id))).data,

  listRepayments: async (facilityId: string): Promise<Repayment[]> => (await axios.get(E.REPAYMENTS(facilityId))).data,
  previewRepayment: async (body: RepaymentRequest): Promise<RepaymentPreview> => (await axios.post(E.REPAYMENT_PREVIEW, body)).data,
  createRepayment: async (body: RepaymentRequest): Promise<Repayment> => (await axios.post(E.REPAYMENT_CREATE, body)).data,
  voidRepayment: async (id: string): Promise<Repayment> => (await axios.post(E.REPAYMENT_VOID(id))).data,

  runInterest: async (institutionId: string, period: string): Promise<InterestRun> =>
    (await axios.post(E.INTEREST_RUN(institutionId), { period })).data,
  listRuns: async (institutionId: string): Promise<InterestRun[]> => (await axios.get(E.INTEREST_RUNS(institutionId))).data,
  getRun: async (runId: string): Promise<InterestRun> => (await axios.get(E.INTEREST_RUN_GET(runId))).data,
  interestRegister: async (institutionId: string, from: string, to: string): Promise<InterestLine[]> =>
    (await axios.get(E.INTEREST_REGISTER(institutionId), { params: { from, to } })).data,
  voidInterestLine: async (id: string): Promise<InterestLine> => (await axios.post(E.INTEREST_LINE_VOID(id))).data,

  utilisation: async (institutionId: string, asOf?: string): Promise<UtilisationReport> =>
    (await axios.get(E.UTILISATION(institutionId), asOfParams(asOf))).data,
  odPosition: async (institutionId: string, asOf?: string): Promise<OdPositionReport> =>
    (await axios.get(E.OD_POSITION(institutionId), asOfParams(asOf))).data,
  maturity: async (institutionId: string, asOf?: string): Promise<MaturityLadderReport> =>
    (await axios.get(E.MATURITY(institutionId), asOfParams(asOf))).data,
  tieOut: async (institutionId: string, asOf?: string): Promise<TieOutReport> =>
    (await axios.get(E.TIE_OUT(institutionId), asOfParams(asOf))).data,
  dueItems: async (institutionId: string, from: string, to: string): Promise<BorrowingDueItem[]> =>
    (await axios.get(E.DUE_ITEMS(institutionId), { params: { from, to } })).data,

  listCovenants: async (institutionId: string): Promise<Covenant[]> => (await axios.get(E.COVENANTS(institutionId))).data,
  evaluateCovenants: async (institutionId: string, asOf?: string): Promise<CovenantReport> =>
    (await axios.get(E.COVENANT_EVALUATE(institutionId), asOfParams(asOf))).data,
  createCovenant: async (body: CovenantRequest): Promise<Covenant> => (await axios.post(E.COVENANT_CREATE, body)).data,
  updateCovenant: async (id: string, body: CovenantRequest): Promise<Covenant> => (await axios.put(E.COVENANT(id), body)).data,
  deleteCovenant: async (id: string): Promise<void> => { await axios.delete(E.COVENANT(id)); },
};
